/**
 * ProductionMarketDataProvider — بيانات حقيقية من الخادم فقط.
 *
 * قواعد ملزمة في هذه الطبقة:
 *   1. **لا سقوط إلى بيانات تجريبية أبداً.** غياب البيانات يُبلَّغ كـ
 *      `insufficient_data`، والميزة غير المطلقة كـ`feature_unavailable`.
 *   2. أي حقل لا يوفّره المصدر يبقى `null` — لا يُملأ بقيمة مشتقة أو مقدّرة.
 *   3. Fair Value و Opportunity Score و Confidence و Market Pressure
 *      **غير مطلقة** في PHASE 2A وتُرجع `feature_unavailable`.
 */

import {
  ProviderError,
  type GetDistrictParams,
  type GetMapMetricsParams,
  type MarketDataProvider,
  type WatchlistMutation,
} from '@/domain/market-data-provider';
import type {
  CityName,
  Comparable,
  ScoreComponent,
  VerdictLevel,
  DistrictDetail,
  DistrictSuggestion,
  MapMetrics,
  MarketPulse,
  Opportunity,
  PricePoint,
  ReportListItem,
  ReportPreview,
  ReportType,
  ValuationRequest,
  ValuationResult,
  WatchAlertConditions,
  WatchItem,
  PropertyCategoryAvailability,
} from '@/domain/types';
import { api, ApiError } from '@/lib/api';

// ------------------------------------------------------- أشكال استجابة API

interface ApiLiveMetrics {
  transaction_count: number;
  total_transaction_value: number | null;
  total_area: number | null;
  median_price_per_sqm: number | null;
  average_price_per_sqm: number | null;
  /** الطبقة التي أنتجت هذه الأرقام: صفقات مفردة أم مؤشرات مجمّعة. */
  layer?: 'transactions' | 'indicators';
  period?: string;
  min_price_per_sqm: number | null;
  max_price_per_sqm: number | null;
  latest_transaction_date: string | null;
  price_movement: {
    change_percentage: number;
    period_days: number;
    current_period_count: number;
    previous_period_count: number;
  } | null;
  sufficient: boolean;
  metrics_version: string;
}

interface ApiDistrictMetrics extends ApiLiveMetrics {
  city: string;
  district: string;
  district_id: string;
}

interface ApiStatus {
  mode: 'LIVE' | 'NO_DATA';
  source_id: string | null;
  generated_at: string | null;
  transaction_count: number;
  cities: string[];
  districts_covered: number;
  /** قد يغيب في خادم أقدم — والغياب يُقرأ أرضاً فقط. */
  property_categories?: { LAND: boolean; BUILDING: boolean };
}

interface ApiDistrictEntry {
  city: string;
  district: string;
  district_id: string;
  coverage: 'TRANSACTIONS' | 'LIMITED_TRANSACTIONS' | 'LISTINGS_ONLY';
  transaction_count: number;
  listing_count: number;
  median_price_per_sqm: number | null;
  valuation_available: boolean;
  coverage_note: string | null;
}

interface ApiEvaluation {
  status: 'VALUED' | 'INSUFFICIENT_DATA' | 'INVALID_INPUT';
  fair_value: {
    fair_value_per_sqm_low: number;
    fair_value_per_sqm_point: number;
    fair_value_per_sqm_high: number;
    fair_value_total_low: number;
    fair_value_total_point: number;
    fair_value_total_high: number;
    selected_comparable_count: number;
    effective_comparable_count: number;
    model_version: string;
  } | null;
  confidence: {
    score: number;
    band: 'HIGH' | 'MEDIUM' | 'LOW';
    components: Record<string, number>;
    strengths: string[];
    limitations: string[];
    interpretation: string;
    model_version: string;
  } | null;
  opportunity: {
    score: number;
    band: 'STRONG_SIGNAL' | 'MODERATE_SIGNAL' | 'WEAK_SIGNAL' | 'NO_ADVANTAGE';
    price_position_pct: number;
    asking_price_per_sqm: number;
    components: { key: string; label: string; value: number | null; detail: string }[];
    model_version: string;
  } | null;
  market_pressure: { buyer_power: number; band: string; interpretation: string } | null;
  location_mode: 'PRECISE' | 'DISTRICT_LEVEL' | 'DISTRICT_FALLBACK';
  scope_label: string;
  scope_basis: string;
  scope_warning: string | null;
  location_strategy?: { user_notice: string | null };
  comparables: {
    selected_comparables: {
      transaction_id: string;
      transaction_date: string;
      area_sqm: number;
      distance_m: number | null;
      executed_price_per_sqm: number;
      comparable_score: number;
    }[];
    quality_summary: { average_age_days: number | null };
  };
  explanation: {
    headline: string;
    sections: { key: string; title: string; sentences: string[] }[];
    limitations: string[];
    disclaimer: string;
  };
  insufficiency_reasons: string[];
  rejection: { headline: string; facts: string[]; suggestion: string | null } | null;
}

/** يحوّل مغلّف الخادم إلى عقد التقييم الذي تفهمه الواجهة. */
function toValuationResult(request: ValuationRequest, res: ApiEvaluation): ValuationResult {
  const fv = res.fair_value!;
  const cf = res.confidence!;
  const op = res.opportunity!;

  const verdict: VerdictLevel =
    op.band === 'STRONG_SIGNAL'
      ? 'strong'
      : op.band === 'MODERATE_SIGNAL'
        ? 'good'
        : op.band === 'WEAK_SIGNAL'
          ? 'fair'
          : 'above-market';

  // أسباب النتيجة = جمل EXP-1.0 كما هي. الواجهة لا تصوغ نصاً.
  const reasons = res.explanation.sections
    .filter((s) => s.key === 'fair_value' || s.key === 'price_position')
    .flatMap((s) => s.sentences)
    .slice(0, 5);

  const components: ScoreComponent[] = op.components
    .filter((c) => c.value !== null)
    .map((c) => ({ key: c.key, label: c.label, value: c.value!, max: 100, note: c.detail }));

  const comparables: Comparable[] = res.comparables.selected_comparables.map((c) => ({
    id: c.transaction_id,
    date: c.transaction_date,
    areaSqm: c.area_sqm,
    pricePerSqm: c.executed_price_per_sqm,
    deltaPct: +(((c.executed_price_per_sqm - op.asking_price_per_sqm) / op.asking_price_per_sqm) * 100).toFixed(1),
    distanceLabel: c.distance_m === null ? '—' : `${(c.distance_m / 1000).toFixed(1)} كم`,
  }));

  return {
    property_id: `live:${request.city}|${request.district}|${request.areaSqm}`,
    city: request.city,
    district: request.district,
    area_sqm: request.areaSqm,
    asking_price: request.askingPrice,
    asking_price_per_sqm: op.asking_price_per_sqm,
    fair_value_low: fv.fair_value_total_low,
    fair_value_high: fv.fair_value_total_high,
    difference_percentage: op.price_position_pct,
    opportunity_score: op.score,
    confidence_score: cf.score,
    comparables_count: fv.selected_comparable_count,
    data_age: Math.round(res.comparables.quality_summary.average_age_days ?? 0),
    valuation_model_version: fv.model_version as ValuationResult['valuation_model_version'],
    confidence_model_version: cf.model_version as ValuationResult['confidence_model_version'],
    generated_at: new Date().toISOString(),
    verdict,
    headline: res.explanation.headline,
    confidence_explanation: cf.interpretation,
    reasons,
    score_components: components,
    comparables,
    district_id: `${request.city}|${request.district}`,
    location_mode: res.location_mode,
    scope_label: res.scope_label,
    scope_basis: res.scope_basis,
    scope_warning: res.scope_warning,
    location_notice: res.location_strategy?.user_notice ?? null,
    quality: {
      origin: 'production',
      dataAgeDays: Math.round(res.comparables.quality_summary.average_age_days ?? 0),
      comparablesCount: fv.selected_comparable_count,
      comparablesExpected: 6,
      partial: fv.effective_comparable_count < 6,
    },
  };
}

interface ApiWatchRow {
  district_id: string;
  city: string;
  district: string;
  added_at: string;
  metrics: ApiDistrictMetrics | null;
}

const COMING_SOON = (feature: string): ProviderError =>
  new ProviderError('feature_unavailable', `${feature} قيد الإطلاق — لم يُطلق محرّكه بعد.`);

interface ApiPressure {
  status: string;
  buyer_power: number | null;
  band: string | null;
  interpretation: string;
  model_version: string;
}

function toDistrictDetail(
  metrics: ApiDistrictMetrics,
  series: { month: string; median_price_per_sqm: number; transaction_count: number }[],
  transactions: {
    id: string;
    transacted_at: string;
    area_sqm: number;
    price: number;
    price_per_sqm: number;
  }[],
  pressure?: ApiPressure | null,
): DistrictDetail {
  const points: PricePoint[] = series.map((p, i) => ({
    t: i,
    label: p.month,
    deal: p.median_price_per_sqm,
    // سعر العرض يحتاج مصدر إعلانات — لا يُخترع. نساويه بسعر التنفيذ حتى لا
    // يرسم المخطط خطاً وهمياً، والواجهة تعرض «غير متاح» في بطاقة سعر العرض.
    ask: p.median_price_per_sqm,
  }));

  return {
    id: metrics.district_id,
    name: metrics.district,
    city: metrics.city,
    zone: '',
    lat: 0,
    lng: 0,
    // الوسيط أدق، لكنه غير متاح من طبقة المؤشرات — عندها يُستخدم المتوسط.
    dealPricePerSqm: metrics.median_price_per_sqm ?? metrics.average_price_per_sqm,
    askPricePerSqm: null,
    askDealGapPct: null,
    fairValueLowPerSqm: null,
    fairValueHighPerSqm: null,
    opportunityScore: null,
    confidenceScore: null,
    details: {
      deals90d: metrics.transaction_count,
      supply: null,
      avgDaysOnMarket: null,
      priceCutRate: null,
      liquidityScore: null,
      annualGrowth: metrics.price_movement?.change_percentage ?? null,
    },
    // MP-1.0 حقيقي حين تتوفر بيانات معروض كافية، و`null` صراحةً حين لا تتوفر.
    pressure:
      pressure && pressure.status === 'MEASURED' && pressure.buyer_power !== null
        ? {
            buyerPower: pressure.buyer_power,
            statement: pressure.interpretation,
            modelVersion: pressure.model_version as never,
          }
        : null,
    quality: {
      origin: 'production',
      dataAgeDays: metrics.latest_transaction_date
        ? Math.max(
            0,
            Math.round(
              (Date.now() - new Date(`${metrics.latest_transaction_date}T00:00:00Z`).getTime()) /
                86_400_000,
            ),
          )
        : 0,
      comparablesCount: metrics.transaction_count,
      comparablesExpected: 6,
      partial: !metrics.sufficient,
    },
    series: points,
    recentDeals: transactions.map((t) => ({
      id: t.id,
      date: t.transacted_at,
      areaSqm: t.area_sqm,
      pricePerSqm: t.price_per_sqm,
      totalPrice: t.price,
      street: '',
    })),
    live: {
      transactionCount: metrics.transaction_count,
      totalTransactionValue: metrics.total_transaction_value,
      totalArea: metrics.total_area,
      medianPricePerSqm: metrics.median_price_per_sqm,
      averagePricePerSqm: metrics.average_price_per_sqm,
      minPricePerSqm: metrics.min_price_per_sqm,
      maxPricePerSqm: metrics.max_price_per_sqm,
      latestTransactionDate: metrics.latest_transaction_date,
      priceMovementPct: metrics.price_movement?.change_percentage ?? null,
      priceMovementPeriodDays: metrics.price_movement?.period_days ?? null,
      sufficient: metrics.sufficient,
      metricsVersion: metrics.metrics_version,
    },
  };
}

export class ProductionMarketDataProvider implements MarketDataProvider {
  readonly origin = 'production' as const;
  readonly label = 'Production Market Data Provider (official transactions, server-side)';

  private statusCache: ApiStatus | null = null;

  async getStatus(): Promise<ApiStatus> {
    if (!this.statusCache) {
      this.statusCache = await api<ApiStatus>('/market/status');
    }
    return this.statusCache;
  }

  async getCities(): Promise<CityName[]> {
    return (await this.getStatus()).cities;
  }

  async getPropertyCategories(): Promise<PropertyCategoryAvailability> {
    const categories = (await this.getStatus()).property_categories;
    return { LAND: categories?.LAND ?? true, BUILDING: categories?.BUILDING ?? false };
  }

  async searchDistricts(query: string, limit = 6): Promise<DistrictSuggestion[]> {
    const q = query.trim();
    if (!q) return [];
    const { districts } = await api<{ districts: ApiDistrictEntry[] }>(
      `/market/districts?q=${encodeURIComponent(q)}`,
    );
    // كل حي معروف يظهر — ومعه حالة تغطيته حتى لا يُقرأ الظهور كوعد بتقييم.
    return districts.slice(0, limit).map((d) => ({
      districtId: d.district_id,
      name: d.district,
      city: d.city,
      dealPricePerSqm: d.median_price_per_sqm ?? 0,
      valuationAvailable: d.valuation_available,
      coverageNote: d.coverage_note,
    }));
  }

  async getDistrict(params: GetDistrictParams): Promise<DistrictDetail> {
    const id = params.districtId ?? (params.city && params.name ? `${params.city}|${params.name}` : '');
    if (!id) throw new ProviderError('not_found', 'الحي غير محدد.');
    try {
      const res = await api<{
        metrics: ApiDistrictMetrics;
        series: { month: string; median_price_per_sqm: number; transaction_count: number }[];
        transactions: {
          id: string;
          transacted_at: string;
          area_sqm: number;
          price: number;
          price_per_sqm: number;
        }[];
        pressure?: ApiPressure | null;
      }>(`/market/district/${encodeURIComponent(id)}`);
      return toDistrictDetail(res.metrics, res.series, res.transactions, res.pressure ?? null);
    } catch (err) {
      if (err instanceof ApiError && err.status === 404) {
        throw new ProviderError('insufficient_data', 'لا توجد صفقات مسجّلة لهذا الحي بعد.');
      }
      throw new ProviderError('unavailable', 'تعذّر الوصول إلى بيانات الحي.');
    }
  }

  async getMapMetrics(params: GetMapMetricsParams): Promise<MapMetrics> {
    // النمط الوحيد المشتق من الصفقات الحقيقية هو السعر.
    if (params.mode !== 'price') {
      throw COMING_SOON('طبقة الخريطة لهذا النمط');
    }
    const { cells } = await api<{
      cells: {
        district_id: string;
        district: string;
        city: string;
        median_price_per_sqm: number | null;
        transaction_count: number;
      }[];
    }>(`/market/map?city=${encodeURIComponent(params.city)}`);

    const values = cells
      .map((c) => c.median_price_per_sqm)
      .filter((v): v is number => v !== null)
      .sort((a, b) => a - b);

    return {
      mode: 'price',
      modeLabel: 'وسيط سعر المتر',
      unit: 'ر.س/م²',
      legendLow: values[0] ?? null,
      legendHigh: values[values.length - 1] ?? null,
      cells: cells.map((c) => ({
        districtId: c.district_id,
        name: c.district,
        city: c.city,
        lat: 0,
        lng: 0,
        value: c.median_price_per_sqm,
      })),
      quality: {
        origin: 'production',
        dataAgeDays: 0,
        comparablesCount: cells.length,
        comparablesExpected: 6,
        partial: cells.length === 0,
      },
    };
  }

  /**
   * التقييم الكامل: CMP → FV → CF → OP → MP → EXP على الخادم.
   * الواجهة لا تحسب شيئاً — تستقبل العقد جاهزاً.
   */
  async getValuation(request: ValuationRequest): Promise<ValuationResult> {
    let res: ApiEvaluation;
    try {
      res = await api<ApiEvaluation>('/market/evaluate', {
        method: 'POST',
        body: {
          city: request.city,
          district: request.district,
          area_sqm: request.areaSqm,
          asking_price: request.askingPrice,
          property_category: request.propertyCategory ?? 'LAND',
          latitude: request.latitude ?? null,
          longitude: request.longitude ?? null,
        },
      });
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        throw new ProviderError('invalid_input', 'مدخلات غير صالحة.');
      }
      throw new ProviderError('unavailable', 'تعذّر الوصول إلى محرك التقييم.');
    }

    // «لا توجد بيانات كافية» حالة أولى الدرجة، لا خطأ.
    if (res.status !== 'VALUED' || !res.fair_value || !res.opportunity || !res.confidence) {
      throw new ProviderError(
        'insufficient_data',
        res.rejection?.headline ??
          res.explanation?.headline ??
          'لا توجد بيانات كافية لإصدار نطاق قيمة موثوق.',
        res.rejection ?? null,
      );
    }
    return toValuationResult(request, res);
  }

  async getOpportunities(): Promise<Opportunity[]> {
    // ترتيب الفرص يحتاج تقييم كل حي على حدة — لم يُفعّل بعد كقائمة جاهزة.
    throw COMING_SOON('قائمة الفرص المرتّبة');
  }

  // ------------------------------------------------------------- المراقبة

  private mapWatchlist(items: ApiWatchRow[]): WatchItem[] {
    return items.map((row) => ({
      id: row.district_id,
      districtId: row.district_id,
      district: row.district,
      city: row.city,
      addedAt: row.added_at.slice(0, 10),
      dealPricePerSqm: row.metrics?.median_price_per_sqm ?? 0,
      changePct: row.metrics?.price_movement?.change_percentage ?? 0,
      opportunityScore: 0,
      conditions: {
        priceDrop: false,
        newDeals: false,
        supplyChange: false,
        opportunityJump: false,
      },
    }));
  }

  private async call(
    path: string,
    method: 'GET' | 'POST' | 'DELETE',
    body?: unknown,
  ): Promise<WatchItem[]> {
    try {
      const res = await api<{ items: ApiWatchRow[] }>(path, { method, body });
      return this.mapWatchlist(res.items);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        throw new ProviderError('unavailable', 'يلزم تسجيل الدخول لحفظ قائمة المراقبة.');
      }
      throw new ProviderError('unavailable', 'تعذّر الوصول إلى قائمة المراقبة.');
    }
  }

  async getWatchlist(): Promise<WatchItem[]> {
    return this.call('/watchlist', 'GET');
  }

  async addToWatchlist(m: WatchlistMutation): Promise<WatchItem[]> {
    return this.call('/watchlist', 'POST', { district_id: m.districtId });
  }

  async removeFromWatchlist(districtId: string): Promise<WatchItem[]> {
    return this.call(`/watchlist/${encodeURIComponent(districtId)}`, 'DELETE');
  }

  async updateWatchConditions(_districtId: string, _c: WatchAlertConditions): Promise<WatchItem[]> {
    // التنبيهات خارج نطاق PHASE 2A — لا شروط تُحفظ بعد.
    throw COMING_SOON('شروط التنبيه');
  }

  // ---------------------------------------------------------- نبض السوق

  async getMarketPulse(city?: CityName): Promise<MarketPulse> {
    const { metrics } = await api<{ metrics: ApiLiveMetrics }>(
      `/market/overview${city ? `?city=${encodeURIComponent(city)}` : ''}`,
    );

    const dash = '—';
    const fmt = (v: number | null, suffix = ''): string =>
      v === null ? dash : `${new Intl.NumberFormat('en-US').format(Math.round(v))}${suffix}`;

    return {
      rows: [
        { label: 'عدد الصفقات المسجّلة', value: fmt(metrics.transaction_count), tone: 'neutral' },
        {
          label: metrics.median_price_per_sqm !== null ? 'وسيط سعر المتر' : 'متوسط سعر المتر',
          value: fmt(
            metrics.median_price_per_sqm ?? metrics.average_price_per_sqm,
            ' ر.س/م²',
          ),
          tone: 'neutral',
        },
        {
          label: 'حركة السعر بين الفترتين',
          value:
            metrics.price_movement === null
              ? dash
              : `${metrics.price_movement.change_percentage > 0 ? '+' : ''}${metrics.price_movement.change_percentage}%`,
          tone:
            metrics.price_movement === null
              ? 'neutral'
              : metrics.price_movement.change_percentage >= 0
                ? 'up'
                : 'down',
        },
        {
          label: metrics.latest_transaction_date !== null ? 'آخر صفقة مسجّلة' : 'أحدث فترة منشورة',
          value: metrics.latest_transaction_date ?? metrics.period ?? dash,
          tone: 'neutral',
        },
      ],
      alerts: [],
      quality: {
        origin: 'production',
        dataAgeDays: 0,
        comparablesCount: metrics.transaction_count,
        comparablesExpected: 6,
        partial: !metrics.sufficient,
      },
    };
  }

  // ------------------------------------------------------------- التقارير

  async listReports(): Promise<ReportListItem[]> {
    return [];
  }

  async getReport(_type: ReportType): Promise<ReportPreview> {
    throw COMING_SOON('التقارير');
  }
}
